import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect, useHistory } from "react-router-dom";
import {
  FaHeadset,
  FaHome,
  FaLanguage,
  FaPhoneAlt,
  FaSyncAlt,
  FaWhatsapp,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import "../css/Admin_css/HelpDesk.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Created By Cursor
function initials(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || "")
    .join("")
    .toUpperCase();
}

// Created By Cursor
function isFlagOn(value) {
  const v = String(value ?? "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "y";
}

// Created By Cursor
function phoneDigits(value) {
  return String(value || "").replace(/[^\d+]/g, "");
}

// Created By Cursor
function telHref(number) {
  const digits = phoneDigits(number);
  return digits ? `tel:${digits}` : "";
}

// Created By Cursor
function whatsappHref(number) {
  const digits = String(number || "").replace(/\D/g, "");
  return digits ? `https://api.whatsapp.com/send?phone=${digits}` : "";
}

// Created By Cursor
export default function HelpDesk() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");

  const loadContacts = useCallback(async ({ soft = false } = {}) => {
    if (soft) setRefreshing(true);
    else setLoading(true);
    try {
      const response = await axios
        .get(`${API_BASE()}getContactUsList.php`, {
          params: {},
        })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        setContacts(Array.isArray(response.data) ? response.data : []);
      } else {
        setContacts([]);
        const remark = String(response?.api_remark || "");
        if (!/no record/i.test(remark)) {
          myToast.notify_warning(remark || "Could not load help desk contacts.");
        }
      }
    } catch (error) {
      console.error(error);
      setContacts([]);
      myToast.notify_warning("Failed to load help desk contacts.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    loadContacts();
  }, [loggedIn, loadContacts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((item) => {
      const hay = [
        item.contact_name,
        item.contact_number,
        item.designation,
        item.languages,
        item.on_call,
        item.on_whatsapp,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [contacts, search]);

  const busy = loading || refreshing;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page hd-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <span style={{ color: COLORS.white }}>Help Desk</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell hd-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaHeadset />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Help Desk</h1>
              <p className="sm-hero__sub">
                {filtered.length
                  ? `${filtered.length} contact${
                      filtered.length === 1 ? "" : "s"
                    } available`
                  : "Support contacts you can call or message"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <div className="sm-hero__search-group">
              <input
                type="search"
                className="sm-hero__search"
                placeholder="Search contacts…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search contacts"
              />
            </div>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadContacts({ soft: true })}
              disabled={busy}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="hd-body">
          {loading && !filtered.length ? (
            <div className="hd-loading">
              <MyLoader />
            </div>
          ) : null}

          {!loading && !filtered.length ? (
            <div className="hd-empty">
              <div className="hd-empty__mark" aria-hidden>
                <FaHeadset />
              </div>
              <p className="hd-empty__title">
                {search.trim() ? "No matching contacts" : "No contacts yet"}
              </p>
              <p className="hd-empty__hint">
                {search.trim()
                  ? "Try a different name, number or language."
                  : "Active help desk contacts will appear here."}
              </p>
            </div>
          ) : null}

          {filtered.length ? (
            <div className="hd-list">
              {filtered.map((item, index) => {
                const name = item.contact_name || "Contact";
                const number = item.contact_number || "";
                const callUrl = telHref(number);
                const waUrl = whatsappHref(number);
                const canCall = Boolean(callUrl);
                const canWhatsapp = Boolean(waUrl);
                return (
                  <article
                    key={item.contact_id || `${name}-${index}`}
                    className="hd-card"
                  >
                    <div className="hd-card__top">
                      <div className="hd-avatar" aria-hidden>
                        {initials(name)}
                      </div>
                      <div className="hd-card__who">
                        <strong>{name}</strong>
                        <small>
                          {item.designation || "Support"}
                          {item.contact_id ? ` · ${item.contact_id}` : ""}
                        </small>
                      </div>
                      <div className="hd-card__tags">
                        {item.designation ? (
                          <span className="hd-pill">{item.designation}</span>
                        ) : null}
                        <span
                          className={`hd-pill${
                            isFlagOn(item.on_call) ? " hd-pill--on" : ""
                          }`}
                        >
                          Call {isFlagOn(item.on_call) ? "on" : "off"}
                        </span>
                        <span
                          className={`hd-pill${
                            isFlagOn(item.on_whatsapp) ? " hd-pill--wa" : ""
                          }`}
                        >
                          WhatsApp {isFlagOn(item.on_whatsapp) ? "on" : "off"}
                        </span>
                      </div>
                    </div>

                    {item.languages ? (
                      <p className="hd-card__langs">
                        <FaLanguage aria-hidden />
                        <span>{item.languages}</span>
                      </p>
                    ) : null}

                    <div className="hd-card__number">
                      <FaPhoneAlt aria-hidden />
                      <span>{number || "—"}</span>
                    </div>

                    <footer className="hd-card__foot">
                      {canCall ? (
                        <a className="hd-btn hd-btn--call" href={callUrl}>
                          <FaPhoneAlt aria-hidden /> Call
                        </a>
                      ) : null}
                      {canWhatsapp ? (
                        <a
                          className="hd-btn hd-btn--wa"
                          href={waUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <FaWhatsapp aria-hidden /> WhatsApp
                        </a>
                      ) : null}
                    </footer>
                  </article>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
